/**
 * Shared Interactive Garage vendor bind helpers (no module self-boot).
 * Used by interactive_garage.js (list/legacy PDP) and product_media rebind (React PDP).
 */

const { CONSENT_GROUP_FUNCTIONAL } = require('public/js/constants/consent-groups.js');

const VENDOR_BINDER_RETRY_INTERVAL_MS = 100;
const VENDOR_BINDER_RETRY_MAX_ATTEMPTS = 50;

/**
 * @param {JQueryStatic} rp$
 * @returns {boolean}
 */
function isInteractiveGarageBinderReady(rp$) {
	return typeof rp$.igViewOnVehicle === 'function' || typeof globalThis.igViewOnVehicle === 'function';
}

/**
 * @param {JQueryStatic} rp$
 * @returns {string|null}
 */
function getDealerCode(rp$) {
	const dealerCodeFromButton = rp$('.find-dealer-button[data-ig-dealer-code]').attr('data-ig-dealer-code');
	if (typeof dealerCodeFromButton === 'string' && dealerCodeFromButton.length > 0) {
		return dealerCodeFromButton;
	}

	const dealerCodeFromVehicle = rp$('.ig-vov-vehicle[data-ig-dealer-code]').attr('data-ig-dealer-code');
	if (typeof dealerCodeFromVehicle === 'string' && dealerCodeFromVehicle.length > 0) {
		return dealerCodeFromVehicle;
	}

	const igUrl = rp$('.ig-view-on-vehicle-wrapper[data-ig-url]').attr('data-ig-url');
	if (typeof igUrl === 'string') {
		const dealerCodePattern = /\/(ig\d+)\/?$/i;
		const match = dealerCodePattern.exec(igUrl);
		if (match) {
			return match[1];
		}
	}

	return null;
}

/**
 * @param {JQueryStatic} rp$
 * @param {'product'|'list'} targetViewType
 * @returns {void}
 */
function initializeInteractiveGarage(rp$, targetViewType) {
	const igSettings = typeof igViewOnVehicleSettings === 'undefined' ? undefined : igViewOnVehicleSettings;
	if (typeof rp$.igViewOnVehicle === 'function') {
		rp$.igViewOnVehicle(targetViewType, getDealerCode(rp$), igSettings);
		return;
	}

	// Fallback to global igViewOnVehicle if rp-scoped function is not available
	if (typeof igViewOnVehicle === 'function') {
		igViewOnVehicle(targetViewType, getDealerCode(rp$), igSettings);
	}
}

/**
 * Runs callback once vendor IG scripts are available (including after consent-gated script activation).
 *
 * @param {JQueryStatic} rp$
 * @param {() => void} callback
 * @returns {() => void} Cancel pending wait (does not run callback).
 */
function whenInteractiveGarageBinderReady(rp$, callback) {
	if (isInteractiveGarageBinderReady(rp$)) {
		callback();
		return function () {};
	}

	let finished = false;
	let pollIntervalId = null;

	const teardownLateLoadListeners = function () {
		globalThis.document.removeEventListener('load', onScriptLoad, true);
	};

	const cancel = function () {
		finished = true;
		if (pollIntervalId !== null) {
			clearInterval(pollIntervalId);
			pollIntervalId = null;
		}
		teardownLateLoadListeners();
	};

	const runWhenReady = function () {
		if (finished || !isInteractiveGarageBinderReady(rp$)) {
			return;
		}

		finished = true;
		if (pollIntervalId !== null) {
			clearInterval(pollIntervalId);
			pollIntervalId = null;
		}
		teardownLateLoadListeners();
		callback();
	};

	const onScriptLoad = function () {
		runWhenReady();
	};

	globalThis.document.addEventListener('load', onScriptLoad, true);

	let pollAttempts = 0;
	pollIntervalId = setInterval(function () {
		pollAttempts += 1;
		runWhenReady();
		if (finished || pollAttempts >= VENDOR_BINDER_RETRY_MAX_ATTEMPTS) {
			if (pollIntervalId !== null) {
				clearInterval(pollIntervalId);
				pollIntervalId = null;
			}
		}
	}, VENDOR_BINDER_RETRY_INTERVAL_MS);

	const consent = globalThis.rp_app?.consent;
	if (consent && typeof consent.onAllowedConsentCategoriesChange === 'function') {
		consent.onAllowedConsentCategoriesChange(function (allowedConsentCategories) {
			if (allowedConsentCategories.includes(CONSENT_GROUP_FUNCTIONAL)) {
				runWhenReady();
			}
		});
	}

	return cancel;
}

module.exports = {
	getDealerCode,
	initializeInteractiveGarage,
	isInteractiveGarageBinderReady,
	whenInteractiveGarageBinderReady,
};
